'use client';

import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import { openErrorModal } from '@/lib/redux/slices/errorModalSlice';
import { selectUsageData } from '@/lib/redux/slices/usageSlice';
import { cn } from '@/lib/utils';
import { AnimatePresence, m } from 'framer-motion';
import { Lock, Sparkles, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'trial_banner_dismissed_at';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number.parseInt(raw, 10);
    return Date.now() - dismissedAt < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

function dismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/**
 * Slim trial/freemium banner shown on app pages.
 *
 * Active trial ("Skúšate Pro"):
 *   "Skúšate Pro — ešte {days} dní · {remaining}/{cap} zadaní"  + See plans button
 *
 * Expired/freemium (free-floor framing):
 *   "Máte k dispozícii Hľadať právne zdroje bez obmedzenia + 5 Priamych zadaní/mes · cez web aj cez MCP"  + Subscribe CTA
 *
 * Dismissible for 24 hours. Hidden for paid subscribers.
 */
export function TrialBanner({ className }: { className?: string }) {
  const t = useTranslations('TrialBanner');
  const dispatch = useAppDispatch();
  const usageData = useAppSelector(selectUsageData);
  const zadanie = usageData?.zadanie ?? null;

  const [dismissed, setDismissed] = useState(true); // Start hidden to avoid flash

  useEffect(() => {
    setDismissed(isDismissed());
  }, []);

  const handleDismiss = useCallback(() => {
    dismiss();
    setDismissed(true);
  }, []);

  const handleSubscribe = useCallback(() => {
    dispatch(
      openErrorModal({
        errorCode: 402,
        source: 'chat',
        isManualOpen: true,
      })
    );
  }, [dispatch]);

  if (!zadanie) return null;

  // Hide banner while the welcome modal is showing (one message at a time)
  if (usageData?.onboarding?.show_welcome_modal) return null;

  // Don't show for paid subscribers
  const isTrial =
    zadanie.plan.plan_type === 'trial' ||
    zadanie.plan.name.toLowerCase() === 'trial';
  if (!isTrial) return null;

  if (dismissed) return null;

  const inTrialBoost = zadanie.trial.in_trial && !zadanie.trial.expired;
  // Monthly remaining from zadanie; for the freemium case the banner doesn't
  // show detailed numbers — just the free-floor value proposition.
  const tasksRemaining = zadanie.monthly.remaining ?? 0;
  const tasksCap = zadanie.monthly.soft_cap ?? 0;

  return (
    <AnimatePresence>
      <m.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
        className={cn('rounded-lg text-sm overflow-hidden', className)}
        style={{ background: 'var(--gradient-button)' }}
      >
        {/* #8795/#8797: this whole banner sits directly on
            [background:var(--gradient-button)] (the `style` above).
            `text-content-inverse` (var(--text-inverse)) is near-black
            (#141413) in the default/calm/warm dark palettes — same failure
            class as button.tsx/error.tsx. Full-opacity nodes use
            `text-cta-text` (var(--cta-text), solid #ffffff in all 8
            palette/mode combos, #8796).

            #8797: classified each translucent node by what WCAG actually
            requires of it, rather than uniformly de-opacifying:
              • boostValue is real TEXT (1.4.3, 4.5:1) → full-opacity
                `text-cta-text`; keeps `text-xs` so it stays subordinate to
                the headline by size rather than by opacity.
              • the separator "·" is DECORATIVE → `aria-hidden`, which
                exempts it from contrast entirely (not 4.5:1, not even 3:1),
                so it keeps its `/70` de-emphasis.
              • Sparkles (`/90`) and Lock (`/70`) are NON-text (1.4.11,
                3:1) — measured, already clear it in all 8 combos (worst
                4.16:1 and 3.12:1), so untouched.
            Measured at the worst of 200 gradient-interpolation steps:
            `/50` = 2.22–2.88 (fails even 3:1), `/70` = 3.12–4.06 (clears
            3:1, fails 4.5:1), full = 4.73–6.31 (clears 4.5:1). */}
        <div className='flex items-center justify-between gap-3 px-4 py-2.5'>
          <div className='flex items-center gap-3 min-w-0'>
            {inTrialBoost ? (
              /* ── Active Pro trial ── */
              <>
                <Sparkles className='w-4 h-4 text-cta-text/90 shrink-0' />
                <div className='flex items-center gap-2 text-cta-text'>
                  <span className='font-medium'>
                    {t('boostHeadline', {
                      days: zadanie.trial.days_remaining ?? 0,
                    })}
                  </span>
                  {/* Purely decorative glyph between two already-distinct
                      spans — aria-hidden removes it from the a11y tree, so it
                      carries NO WCAG contrast requirement (not 4.5:1, not even
                      the 3:1 non-text floor). That lets it keep the /70
                      opacity de-emphasis instead of trading the banner's
                      visual hierarchy for a threshold it needn't meet. */}
                  <span
                    className='text-cta-text/70 hidden sm:inline'
                    aria-hidden='true'
                  >
                    ·
                  </span>
                  <span className='text-cta-text text-xs hidden sm:inline'>
                    {t('boostValue', {
                      remaining: tasksRemaining,
                      cap: tasksCap,
                    })}
                  </span>
                </div>
              </>
            ) : (
              /* ── Freemium / expired trial — free-floor framing ── */
              <>
                <Lock className='w-4 h-4 text-cta-text/70 shrink-0' />
                <span className='text-cta-text font-medium'>
                  {t('freemiumHeadline')}
                </span>
              </>
            )}
          </div>
          <div className='flex items-center gap-2 shrink-0'>
            {/* #8795/#8797: NOT swapped to text-cta-text — this button's real
                backdrop is the translucent bg-white/20 (/30 hover) pill
                composited over the gradient, not the raw gradient itself.
                Measured: swapping the text token here would REGRESS
                currently-passing dark-mode combos (e.g. default-dark
                5.56:1 pass → 3.00:1 fail; warm-dark 6.28:1 pass → 2.85:1
                fail) because white text on this particular light-tinted
                pill is worse than near-black text in those cases. This is
                a distinct bug (the hardcoded bg-white/NN pill itself, not
                --text-inverse resolution) — left as-is; see the PR body for
                the opaque-background options considered and why none of
                them ship cleanly across all 8 combos yet. */}
            <button
              type='button'
              onClick={handleSubscribe}
              className='text-xs font-semibold bg-white/20 hover:bg-white/30 backdrop-blur-sm text-content-inverse px-3.5 py-1.5 rounded-full min-h-[30px] transition-all border border-white/20'
            >
              {inTrialBoost ? t('seePlans') : t('subscribe')}
            </button>
            {/* #8797: the X is a non-text icon control (WCAG 1.4.11, 3:1
                floor) — its resting `/50` failed 3:1 in all 8 combos
                (2.23–2.88:1). Bumped to `/70`, matching the Lock icon's
                already-verified-safe opacity tier (worst case 3.12:1). */}
            <button
              type='button'
              onClick={handleDismiss}
              className='text-cta-text/70 hover:text-cta-text transition-colors p-1 min-w-[28px] min-h-[28px] flex items-center justify-center'
              aria-label={t('dismiss')}
            >
              <X className='w-3.5 h-3.5' />
            </button>
          </div>
        </div>
      </m.div>
    </AnimatePresence>
  );
}
